import Ajax from "./ajax";
import moment from 'moment';
import 'daterangepicker';
import {
	getAllRoles,
	getRoleById,
	getDefaultStaffEntry,
	initializeDefaultTalliesData,
	calculateStaffValues,
	COLUMN_TYPES
} from "./tally-roles-config";

/**
 * Refactored Job Tallies System
 *
 * This version supports role-based tallies with different table layouts
 * per role, hardcoded role definitions, and maintains backwards compatibility.
 */

class JobTalliesManager {
	constructor() {
		this.talliesData = null;
		this.currentRoleId = null;
		this.initialized = false;
		this.filterStartDate = null;
		this.filterEndDate = null;

		// Auto-save and polling configuration
		this.autoSaveInterval = 30000; // 30 seconds
		this.pollInterval = 10000; // 10 seconds for checking updates
		this.autoSaveTimer = null;
		this.pollTimer = null;
		this.lastSavedData = null;
		this.lastSavedNotes = null;
		this.currentVersion = 0;
		this.isDirty = false;
		this.isSaving = false;
		this.jobId = null;
		this.tallyUuid = null;
	}

	/**
	 * Initialize the tallies system
	 */
	init() {
		if (this.initialized) return;

		const talliesElement = document.getElementById('jobTallies');
		if (!talliesElement) {
			return;
		}

		this.initializeTalliesData();
		this.updateNoDatesVisibility();
		this.bindDateChangeEvents();
		this.bindFilterDateEvents();
		this.initializeFilterDates();

		// Wait for staff allocation watcher to complete initial scan
		setTimeout(() => {
			this.renderRoleNavigation();

			// Render all summary tables after staff allocation is complete
			this.renderAllSummaryTables();

			// Set first role with staff as active by default
			const rolesWithStaff = getAllRoles().filter(role => this.roleHasStaff(role.id));
			const firstRoleWithStaff = rolesWithStaff[0];
			if (firstRoleWithStaff) {
				this.switchToRole(firstRoleWithStaff.id);
			}

			// Sync to hidden input to ensure any defaults set during initialization are persisted
			this.syncToHiddenInput();

			// Initialize auto-save and polling
			this.initAutoSave();
		}, 100);

		this.initialized = true;
	}

	/**
	 * Initialize auto-save and polling functionality
	 */
	initAutoSave() {
		// Get job ID from the page
		const jobIdElement = document.getElementById('jobID');
		if (jobIdElement) {
			this.jobId = jobIdElement.textContent.trim();
		}

		// Get tally UUID from URL if present (for tally access users)
		const urlParams = new URLSearchParams(window.location.search);
		this.tallyUuid = urlParams.get('tally');

		// Get current version from hidden input
		const versionInput = document.getElementById('id_job-tallies_version');
		if (versionInput) {
			this.currentVersion = parseInt(versionInput.value) || 0;
		}

		// Store initial data state for dirty checking
		this.lastSavedData = JSON.stringify(this.talliesData);
		const notesInput = document.getElementById('id_job-tally_notes');
		this.lastSavedNotes = notesInput ? notesInput.value : '';

		// Create status indicator
		this.createStatusIndicator();

		// Start auto-save timer
		this.startAutoSave();

		// Start polling for updates
		this.startPolling();

		// Track changes to mark as dirty
		this.bindDirtyTracking();

		// Save before page unload if dirty
		window.addEventListener('beforeunload', (e) => {
			if (this.isDirty) {
				this.autoSave();
			}
		});
	}

	/**
	 * Create the save status indicator element
	 */
	createStatusIndicator() {
		const container = document.querySelector('.job-tallies__date-filter');
		if (!container) return;

		const indicator = document.createElement('span');
		indicator.id = 'tallyAutoSaveStatus';
		indicator.className = 'ms-2 small text-muted';
		indicator.innerHTML = '<i class="far fa-check-circle"></i> Saved';
		indicator.style.display = 'inline-flex';
		indicator.style.alignItems = 'center';
		indicator.style.gap = '4px';
		container.appendChild(indicator);

		this.statusIndicator = indicator;
	}

	/**
	 * Update the status indicator
	 */
	updateStatus(status, message) {
		if (!this.statusIndicator) return;

		const icons = {
			saving: '<i class="far fa-spinner fa-spin"></i>',
			saved: '<i class="far fa-check-circle text-success"></i>',
			error: '<i class="far fa-exclamation-circle text-danger"></i>',
			conflict: '<i class="far fa-exclamation-triangle text-warning"></i>',
			dirty: '<i class="far fa-circle text-muted"></i>'
		};

		this.statusIndicator.innerHTML = `${icons[status] || ''} ${message}`;
	}

	/**
	 * Start auto-save timer
	 */
	startAutoSave() {
		if (this.autoSaveTimer) {
			clearInterval(this.autoSaveTimer);
		}

		this.autoSaveTimer = setInterval(() => {
			if (this.isDirty && !this.isSaving) {
				this.autoSave();
			}
		}, this.autoSaveInterval);
	}

	/**
	 * Start polling for updates from other users
	 */
	startPolling() {
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
		}

		this.pollTimer = setInterval(() => {
			if (!this.isSaving && !this.isDirty) {
				this.checkForUpdates();
			}
		}, this.pollInterval);
	}

	/**
	 * Bind change events to track dirty state
	 */
	bindDirtyTracking() {
		// Track changes in tally tables
		const talliesContainer = document.getElementById('jobTallies');
		if (talliesContainer) {
			talliesContainer.addEventListener('input', () => {
				this.markDirty();
			});
			talliesContainer.addEventListener('change', () => {
				this.markDirty();
			});
		}

		// Track changes in notes
		const notesInput = document.getElementById('id_job-tally_notes');
		if (notesInput) {
			notesInput.addEventListener('input', () => {
				this.markDirty();
			});
		}
	}

	/**
	 * Mark data as dirty (has unsaved changes)
	 */
	markDirty() {
		this.isDirty = true;
		this.updateStatus('dirty', 'Unsaved changes');
	}

	/**
	 * Perform auto-save
	 */
	async autoSave() {
		if (!this.jobId || this.isSaving) return;

		this.isSaving = true;
		this.updateStatus('saving', 'Saving...');

		try {
			const notesInput = document.getElementById('id_job-tally_notes');
			const tallyNotes = notesInput ? notesInput.value : '';

			const response = await Ajax.saveTalliesAuto(
				this.jobId,
				this.talliesData,
				tallyNotes,
				this.currentVersion,
				this.tallyUuid
			);

			if (response.error) {
				throw new Error(response.error);
			}

			if (response.body.conflict) {
				// Handle conflict
				this.handleConflict(response.body);
			} else if (response.body.success) {
				// Update version and saved state
				this.currentVersion = response.body.new_version;
				this.lastSavedData = JSON.stringify(this.talliesData);
				this.lastSavedNotes = tallyNotes;
				this.isDirty = false;

				// Update hidden version input
				const versionInput = document.getElementById('id_job-tallies_version');
				if (versionInput) {
					versionInput.value = this.currentVersion;
				}

				this.updateStatus('saved', 'Saved');
			}
		} catch (error) {
			console.error('Auto-save error:', error);
			this.updateStatus('error', 'Save failed');
		} finally {
			this.isSaving = false;
		}
	}

	/**
	 * Check for updates from other users
	 */
	async checkForUpdates() {
		if (!this.jobId) return;

		try {
			const response = await Ajax.getTallies(this.jobId, this.tallyUuid);

			if (response.error) return;

			const serverVersion = response.body.tallies_version;
			if (serverVersion > this.currentVersion) {
				// Someone else made changes
				this.handleRemoteUpdate(response.body);
			}
		} catch (error) {
			// Silent fail for polling - don't disrupt user
			console.warn('Polling error:', error);
		}
	}

	/**
	 * Handle conflict when saving
	 */
	handleConflict(conflictData) {
		this.updateStatus('conflict', 'Conflict detected');

		// Show conflict modal
		const message = `
			<p><strong>Another user has updated the tally data.</strong></p>
			<p>Your changes conflict with theirs. What would you like to do?</p>
			<ul>
				<li><strong>Keep my changes</strong> - Overwrite the other user's changes with yours</li>
				<li><strong>Load their changes</strong> - Discard your changes and load the latest version</li>
			</ul>
		`;

		if (confirm('Conflict detected: Another user has updated the tally data.\n\nClick OK to keep YOUR changes and overwrite theirs.\nClick Cancel to discard your changes and load the latest version.')) {
			// Force save user's changes
			this.currentVersion = conflictData.current_version;
			this.autoSave();
		} else {
			// Load server data
			this.loadRemoteData(conflictData);
		}
	}

	/**
	 * Handle remote update (another user made changes)
	 */
	handleRemoteUpdate(remoteData) {
		if (this.isDirty) {
			// User has unsaved changes - show warning
			this.updateStatus('conflict', 'Remote update available');

			if (confirm('Another user has updated the tally data.\n\nYou have unsaved changes. Do you want to load the latest version?\n\nClick OK to load latest (your changes will be lost).\nClick Cancel to keep editing (and save to overwrite their changes).')) {
				this.loadRemoteData(remoteData);
			}
		} else {
			// No local changes - auto-load new data
			this.loadRemoteData(remoteData);
			this.updateStatus('saved', 'Updated');
		}
	}

	/**
	 * Load remote data into the UI
	 */
	loadRemoteData(remoteData) {
		// Update tallies data
		this.talliesData = remoteData.tallies_json || remoteData.current_tallies;
		window.talliesData = this.talliesData;
		this.currentVersion = remoteData.tallies_version || remoteData.current_version;
		this.lastSavedData = JSON.stringify(this.talliesData);

		// Update notes
		const notesInput = document.getElementById('id_job-tally_notes');
		if (notesInput) {
			const newNotes = remoteData.tally_notes || remoteData.current_notes || '';
			notesInput.value = newNotes;
			this.lastSavedNotes = newNotes;
		}

		// Update version hidden input
		const versionInput = document.getElementById('id_job-tallies_version');
		if (versionInput) {
			versionInput.value = this.currentVersion;
		}

		// Update hidden tallies input
		this.syncToHiddenInput();

		// Re-render all tables
		this.renderRoleNavigation();
		this.renderAllSummaryTables();
		if (this.currentRoleId) {
			this.renderTablesForRole(this.currentRoleId);
		}

		this.isDirty = false;
		this.updateStatus('saved', 'Loaded latest');
	}

	/**
	 * Stop auto-save and polling (cleanup)
	 */
	stopAutoSave() {
		if (this.autoSaveTimer) {
			clearInterval(this.autoSaveTimer);
			this.autoSaveTimer = null;
		}
		if (this.pollTimer) {
			clearInterval(this.pollTimer);
			this.pollTimer = null;
		}
	}

	/**
	 * Initialize or load existing tallies data
	 */
	initializeTalliesData() {
		// First, check if there's existing data from template or hidden input
		let existingData = null;
		
		// Prioritize window.talliesData (from template) over hidden input
		if (window.talliesData) {
			existingData = window.talliesData;
		} else {
			// Try to load from hidden input as fallback
			const hiddenInput = document.getElementById('id_job-tallies_json') || document.getElementById('talliesData');
			if (hiddenInput && hiddenInput.value) {
				try {
					existingData = JSON.parse(hiddenInput.value);
				} catch {
					// Error parsing existing tallies data from hidden input
				}
			}
		}
		
		if (existingData && existingData.tally_data) {
			// Use existing data as base
			this.talliesData = existingData;
			// Ensure all required roles exist in the existing data
			this.ensureAllRolesExist();
		} else {
			// Create fresh data only if no existing data found
			this.createFreshTalliesData();
		}

		// Store global reference for backwards compatibility
		window.talliesData = this.talliesData;
	}

	/**
	 * Create fresh tallies data structure
	 */
	createFreshTalliesData() {
		const jobDates = this.generateJobDates();
		this.talliesData = initializeDefaultTalliesData(jobDates);
	}

	/**
	 * Check if job has valid dates and update UI visibility accordingly
	 */
	updateNoDatesVisibility() {
		const noDatesMessage = document.getElementById('talliesNoDatesMessage');
		const talliesContent = document.getElementById('talliesContent');
		const dateFilter = document.querySelector('.job-tallies__date-filter');

		if (!noDatesMessage || !talliesContent) return;

		const hasDates = this.talliesData?.tally_data?.length > 0;

		if (hasDates) {
			noDatesMessage.style.display = 'none';
			talliesContent.style.display = '';
			if (dateFilter) dateFilter.style.display = '';
		} else {
			noDatesMessage.style.display = 'block';
			talliesContent.style.display = 'none';
			if (dateFilter) dateFilter.style.display = 'none';
		}
	}

	/**
	 * Merge existing data with new structure
	 */
	mergeExistingData(existingData) {
		if (!existingData || !existingData.tally_data) return;

		// Merge existing staff data into the fresh structure
		existingData.tally_data.forEach(existingDay => {
			// Find matching day in fresh data
			const freshDay = this.talliesData.tally_data.find(day => day.date === existingDay.date);
			if (!freshDay) return;

			// Merge existing role data
			if (existingDay.roles) {
				existingDay.roles.forEach(existingRole => {
					// Find matching role in fresh data
					const freshRole = freshDay.roles.find(r => r.role_id === existingRole.role_id);
					if (!freshRole) return;

					// Preserve existing staff data
					if (existingRole.staff_counts) {
						freshRole.staff_counts = existingRole.staff_counts;
					}

					// Preserve existing runs data
					if (existingRole.runs) {
						freshRole.runs = existingRole.runs;
					}
				});
			}
		});
	}

	/**
	 * Migrate legacy tallies data to new structure
	 */
	migrateLegacyData() {
		if (!this.talliesData || !this.talliesData.tally_data) return;

		// Ensure each day has all hardcoded roles
		this.talliesData.tally_data.forEach(day => {
			if (!day.roles) day.roles = [];

			getAllRoles().forEach(roleConfig => {
				let existingRole = day.roles.find(r => r.role_id === roleConfig.id);

				if (!existingRole) {
					// Add missing role
					existingRole = {
						role_id: roleConfig.id,
						role_name: roleConfig.name,
						runs: roleConfig.allowMultipleRuns ? (roleConfig.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT)?.defaultRuns || []) : [],
						staff_counts: []
					};
					day.roles.push(existingRole);
				}

				// Migrate staff data if needed
				existingRole.staff_counts = existingRole.staff_counts || [];
			});
		});

		// Update roles list to include all hardcoded roles
		this.talliesData.roles = getAllRoles().map(role => ({
			role_id: role.id,
			role_name: role.name
		}));
	}

	/**
	 * Ensure all hardcoded roles exist in data structure
	 */
	ensureAllRolesExist() {
		if (!this.talliesData) return;

		// Update main roles list
		this.talliesData.roles = getAllRoles().map(role => ({
			role_id: role.id,
			role_name: role.name
		}));

		// Ensure each day has all roles
		this.talliesData.tally_data.forEach(day => {
			getAllRoles().forEach(roleConfig => {
				let existingRole = day.roles.find(r => r.role_id === roleConfig.id);

				if (!existingRole) {
					day.roles.push({
						role_id: roleConfig.id,
						role_name: roleConfig.name,
						runs: roleConfig.allowMultipleRuns ? (roleConfig.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT)?.defaultRuns || []) : [],
						staff_counts: []
					});
				}
			});
		});

		// Ensure existing shearing_crutching staff have section-specific runs
		this.ensureSectionSpecificRuns();

		// Ensure section_notes have default values for shearing/crutching runs
		this.ensureSectionNotesDefaults();
	}

	/**
	 * Ensure section_notes have default values for shearing_crutching role runs
	 * This ensures defaults are persisted to the database, not just set during render
	 */
	ensureSectionNotesDefaults() {
		if (!this.talliesData || !this.talliesData.tally_data) return;

		this.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'shearing_crutching');
			if (!roleData || !roleData.runs) return;

			roleData.runs.forEach(run => {
				if (!run.section_notes) {
					run.section_notes = {};
				}

				// Set default for SHEARING if not already set
				if (!run.section_notes['SHEARING']) {
					run.section_notes['SHEARING'] = 'Flock';
				}

				// Set default for CRUTCHING if not already set
				if (!run.section_notes['CRUTCHING']) {
					run.section_notes['CRUTCHING'] = 'B/h';
				}
			});
		});
	}

	/**
	 * Ensure existing shearing_crutching staff have section-specific runs initialized
	 */
	ensureSectionSpecificRuns() {
		this.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'shearing_crutching');
			if (!roleData || !roleData.runs) return;

			roleData.staff_counts.forEach(staff => {
				if (!staff.originalRole) return;

				// Initialize shearing_runs for appropriate roles
				if ((staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers') && !staff.shearing_runs) {
					staff.shearing_runs = roleData.runs.map(run => ({
						run: run.id,
						count: '0'
					}));
				}

				// Initialize crutching_runs for appropriate roles
				if ((staff.originalRole === 'Crutcher' || staff.originalRole === 'Crutchers' || staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers') && !staff.crutching_runs) {
					staff.crutching_runs = roleData.runs.map(run => ({
						run: run.id,
						count: '0'
					}));
				}
			});
		});
	}

	/**
	 * Generate job dates from form inputs
	 */
	generateJobDates() {
		const startEl = document.getElementById('id_job-job_start_date');
		const endEl = document.getElementById('id_job-job_end_date');
		const weekendEl = document.getElementById('id_job-job_runs_on_weekend');

		if (!startEl || !endEl || !weekendEl) return [];

		// Check for empty date values
		if (!startEl.value || !endEl.value) return [];

		const startDate = new Date(startEl.value);
		const endDate = new Date(endEl.value);
		const includeWeekends = weekendEl.checked;

		// Check for invalid dates
		if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return [];

		if (startDate > endDate) return [];

		const dates = [];
		for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
			const dayOfWeek = d.getDay();
			if (!includeWeekends && (dayOfWeek === 0 || dayOfWeek === 6)) {
				continue;
			}

			const dateStr = d.toISOString().split('T')[0].replaceAll('-', '/');
			const displayDate = d.toLocaleDateString('en-AU', {
				weekday: 'long',
				day: '2-digit',
				month: 'long',
				year: 'numeric'
			});

			dates.push({
				date_verbose: displayDate,
				date: dateStr
			});
		}

		return dates;
	}

	/**
	 * Bind date change events to rebuild tallies
	 */
	bindDateChangeEvents() {
		const startEl = document.getElementById('id_job-job_start_date');
		const endEl = document.getElementById('id_job-job_end_date');
		const weekendEl = document.getElementById('id_job-job_runs_on_weekend');

		[startEl, endEl, weekendEl].forEach(el => {
			if (el) {
				el.addEventListener('change', () => this.rebuildTalliesData());
			}
		});
	}

	/**
	 * Initialize filter date inputs with job date range
	 */
	initializeFilterDates() {
		const filterInput = document.getElementById('tallyDateRangeFilter');

		if (!filterInput || !this.talliesData?.tally_data?.length) return;

		// Get the job's date range from tally data
		const dates = this.talliesData.tally_data.map(day => day.date);
		if (dates.length === 0) return;

		// Convert dates from YYYY/MM/DD to moment objects
		const firstDateStr = dates[0]; // e.g., "2024/09/09"
		const lastDateStr = dates[dates.length - 1];

		// Parse using moment with explicit format
		const firstDate = moment(firstDateStr, 'YYYY/MM/DD');
		const lastDate = moment(lastDateStr, 'YYYY/MM/DD');

		// Store job date boundaries for the picker
		this.jobStartDate = firstDateStr;
		this.jobEndDate = lastDateStr;

		// Initialize daterangepicker with job date constraints
		if (typeof $ !== 'undefined' && $.fn.daterangepicker && firstDate.isValid() && lastDate.isValid()) {
			const self = this;
			$('#tallyDateRangeFilter').daterangepicker({
				opens: 'left',
				drops: 'down',
				autoUpdateInput: false,
				parentEl: '#tally',
				startDate: firstDate,
				endDate: lastDate,
				minDate: firstDate,
				maxDate: lastDate,
				locale: {
					format: 'DD/MM/YYYY',
					cancelLabel: 'Clear'
				}
			}, function(start, end) {
				// Update the input display
				this.element.val(start.format('DD/MM/YYYY') + ' - ' + end.format('DD/MM/YYYY'));

				// Store filter dates in YYYY/MM/DD format for comparison
				self.filterStartDate = start.format('YYYY/MM/DD');
				self.filterEndDate = end.format('YYYY/MM/DD');

				// Re-render
				if (self.currentRoleId) {
					self.renderTablesForRole(self.currentRoleId);
				}
				self.renderAllSummaryTables();

				// Update print button URL with date parameters
				self.updateTallyPrintButtonUrl(start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD'));
			});

			// Handle cancel button to clear filter
			$('#tallyDateRangeFilter').on('cancel.daterangepicker', function() {
				$(this).val('');
				self.clearDateFilter();
				// Reset print button URL to no date filter
				self.updateTallyPrintButtonUrl(null, null);
			});
		}
	}

	/**
	 * Update the tally print button URL with date parameters
	 */
	updateTallyPrintButtonUrl(startDate, endDate) {
		const printBtn = document.getElementById('tallyPrintBtn');
		if (!printBtn) return;

		// Get the base URL (without query params)
		const baseUrl = printBtn.href.split('?')[0];

		if (startDate && endDate) {
			printBtn.href = `${baseUrl}?start_date=${startDate}&end_date=${endDate}`;
		} else {
			printBtn.href = baseUrl;
		}
	}

	/**
	 * Bind filter date change events
	 */
	bindFilterDateEvents() {
		// Date range picker events are handled in initializeFilterDates
	}

	/**
	 * Apply the date filter and re-render (called by daterangepicker callback)
	 */
	applyDateFilter() {
		// Re-render current role tables and all summary tables
		if (this.currentRoleId) {
			this.renderTablesForRole(this.currentRoleId);
		}
		this.renderAllSummaryTables();
	}

	/**
	 * Clear the date filter (called by daterangepicker cancel event)
	 */
	clearDateFilter() {
		const filterInput = document.getElementById('tallyDateRangeFilter');

		if (filterInput) {
			filterInput.value = '';
		}

		this.filterStartDate = null;
		this.filterEndDate = null;

		// Re-render current role tables and all summary tables
		if (this.currentRoleId) {
			this.renderTablesForRole(this.currentRoleId);
		}
		this.renderAllSummaryTables();
	}

	/**
	 * Get filtered tally data based on current date filter
	 */
	getFilteredTallyData() {
		if (!this.talliesData?.tally_data) return [];

		// If no filter is set, return all data
		if (!this.filterStartDate && !this.filterEndDate) {
			return this.talliesData.tally_data;
		}

		return this.talliesData.tally_data.filter(day => {
			const dayDate = day.date;

			// Check start date filter
			if (this.filterStartDate && dayDate < this.filterStartDate) {
				return false;
			}

			// Check end date filter
			if (this.filterEndDate && dayDate > this.filterEndDate) {
				return false;
			}

			return true;
		});
	}

	/**
	 * Check if a specific day index should be shown based on filter
	 */
	isDayInFilterRange(dayIndex) {
		if (!this.talliesData?.tally_data?.[dayIndex]) return false;

		const day = this.talliesData.tally_data[dayIndex];
		const dayDate = day.date;

		// If no filter is set, show all days
		if (!this.filterStartDate && !this.filterEndDate) {
			return true;
		}

		// Check start date filter
		if (this.filterStartDate && dayDate < this.filterStartDate) {
			return false;
		}

		// Check end date filter
		if (this.filterEndDate && dayDate > this.filterEndDate) {
			return false;
		}

		return true;
	}

	/**
	 * Rebuild tallies data when dates change
	 */
	rebuildTalliesData() {
		const newJobDates = this.generateJobDates();
		const oldTallyData = this.talliesData.tally_data || [];

		const newTallyData = newJobDates.map(dateInfo => {
			// Try to find existing data for this date
			const existingDay = oldTallyData.find(day =>
				day.date === dateInfo.date
			);

			if (existingDay) {
				// Ensure all roles exist
				getAllRoles().forEach(roleConfig => {
					let existingRole = existingDay.roles.find(r => r.role_id === roleConfig.id);
					if (!existingRole) {
						existingDay.roles.push({
							role_id: roleConfig.id,
							role_name: roleConfig.name,
							runs: roleConfig.allowMultipleRuns ? (roleConfig.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT)?.defaultRuns || []) : [],
							staff_counts: []
						});
					}
				});
				return existingDay;
			} else {
				// Create new day with all roles
				return {
					date_verbose: dateInfo.date_verbose,
					date: dateInfo.date,
					roles: getAllRoles().map(roleConfig => ({
						role_id: roleConfig.id,
						role_name: roleConfig.name,
						runs: roleConfig.allowMultipleRuns ? (roleConfig.columns.find(col => col.type === COLUMN_TYPES.RUN_COUNT)?.defaultRuns || []) : [],
						staff_counts: []
					}))
				};
			}
		});

		this.talliesData.tally_data = newTallyData;
		this.syncToHiddenInput();

		// Update visibility based on whether we have dates
		this.updateNoDatesVisibility();

		// Re-render current role
		if (this.currentRoleId) {
			this.renderTablesForRole(this.currentRoleId);
		}
	}

	/**
	 * Render role navigation tabs
	 */
	renderRoleNavigation() {
		const navEl = document.querySelector('.job-tallies__nav');
		if (!navEl) return;

		navEl.innerHTML = '';

		// Only show roles that have staff
		const rolesWithStaff = getAllRoles().filter(role => this.roleHasStaff(role.id));

		rolesWithStaff.forEach((role, index) => {
			const li = document.createElement('li');
			li.dataset.roleId = role.id;
			if (index === 0) li.classList.add('active');

			const a = document.createElement('a');
			a.textContent = role.name;
			a.addEventListener('click', (e) => {
				e.preventDefault();
				this.switchToRole(role.id);
			});

			li.appendChild(a);
			navEl.appendChild(li);
		});
	}

	/**
	 * Switch to a specific role
	 */
	switchToRole(roleId) {
		// Update navigation
		document.querySelectorAll('.job-tallies__nav li').forEach(li => {
			li.classList.toggle('active', li.dataset.roleId === roleId);
		});

		this.currentRoleId = roleId;
		this.renderTablesForRole(roleId);
	}

	/**
	 * Render tables for a specific role
	 */
	renderTablesForRole(roleId) {
		const roleConfig = getRoleById(roleId);
		if (!roleConfig) return;

		const container = document.getElementById('jobTallies');
		if (!container) return;

		container.innerHTML = '';

		let displayDayNumber = 1;

		this.talliesData.tally_data.forEach((day, dayIndex) => {
			// Skip days not in filter range
			if (!this.isDayInFilterRange(dayIndex)) return;

			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData) return;

			const card = this.createDayCard(day, roleData, roleConfig, dayIndex, displayDayNumber);
			container.appendChild(card);
			displayDayNumber++;
		});

		this.bindTableEvents(roleId);
		this.updateAllCalculations();

		// Update table calculations after rendering
		setTimeout(() => {
			document.querySelectorAll('.job-tallies__table').forEach(table => {
				const tbody = table.querySelector('tbody');
				if (tbody) {
					tbody.querySelectorAll('tr').forEach(row => {
						this.updateRowCalculations(row, roleId);
					});
				}
				this.updateColumnTotals(table, roleId);
			});
		}, 0);

		this.renderSummaryTable();
	}

	/**
	 * Create a day card with role-specific table
	 */
	createDayCard(day, roleData, roleConfig, dayIndex, displayDayNumber = null) {
		const card = document.createElement('div');
		card.className = 'job-tallies__card';

		// Use displayDayNumber for the visible "Day X" label if provided (for filtered views)
		// but use dayIndex for the collapse ID to maintain data binding
		const dayLabel = displayDayNumber !== null ? displayDayNumber : (dayIndex + 1);

		card.innerHTML = `
            <div class="job-tallies__card-header">
                <h4>
                    <small>Day ${dayLabel}</small>
                    ${day.date_verbose}
                </h4>
                <a class="btn btn--plain" data-bs-toggle="collapse" href="#day${dayIndex + 1}"
                    role="button" aria-expanded="true" aria-controls="day${dayIndex + 1}">
                    <i class="far fa-angle-up"></i>
                </a>
            </div>
            <div id="day${dayIndex + 1}" class="job-tallies__card-body collapse show">
                ${this.createRoleTable(roleData, roleConfig, dayIndex)}
            </div>
        `;

		return card;
	}

	/**
	 * Create role-specific table HTML
	 */
	createRoleTable(roleData, roleConfig, dayIndex) {
		// Check if this role has sections (like SHEARING and CRUTCHING)
		if (roleConfig.hasSections && roleConfig.sections) {
			return this.createSectionedTable(roleData, roleConfig, dayIndex);
		}

		// Regular table for other roles
		const hasRuns = roleConfig.allowMultipleRuns;
		const runs = hasRuns ? (roleData.runs || []) : [];

		let tableHTML = '<div class="table-responsive">';
		tableHTML += '<table class="job-tallies__table table"><thead><tr>';

		// Build table headers based on role configuration
		roleConfig.columns.forEach(column => {
			if (column.type === COLUMN_TYPES.RUN_COUNT && hasRuns) {
				// Add run columns
				runs.forEach(run => {
					tableHTML += `<th class="run-col text-center" scope="col">
                        ${run.name}
                        ${roleConfig.hasRunNotes ? `<input type="text" placeholder="Type run notes" value="${run.notes || ''}" data-run-id="${run.id}">` : ''}
                    </th>`;
				});

				if (hasRuns) {
					const buttonText = roleConfig.addButtonText || 'Add Run';
					tableHTML += `<th scope="col"><button type="button" class="add-run btn" data-day-index="${dayIndex}">+ ${buttonText}</button></th>`;
				}
			} else if (column.type !== COLUMN_TYPES.RUN_COUNT) {
				// Add text-center class for specific Presser and Woolhandler columns
				const isPresser = roleConfig.id === 'presser';
				const isWoolhandler = roleConfig.id === 'woolhandler';
				const shouldCenter = (isPresser && (
					column.type === 'total_runs' ||
					column.type === 'total_bales' ||
					column.type === 'total_kilo' ||
					column.type === COLUMN_TYPES.DAY_TALLY
				)) || (isWoolhandler && (
					column.type === 'total_runs' ||
					column.type === COLUMN_TYPES.DAY_TALLY
				));

				const centerClass = shouldCenter ? ' text-center' : '';
				tableHTML += `<th class="${centerClass}" scope="col">${column.label}</th>`;
			}
		});

		tableHTML += '</tr></thead><tbody>';

		// Build table rows
		roleData.staff_counts.forEach((staff, staffIndex) => {
			const removedClass = staff.removed ? ' class="staff-removed"' : '';
			tableHTML += `<tr${removedClass}>`;

			roleConfig.columns.forEach(column => {
				const cellHTML = this.createTableCell(column, staff, staffIndex, dayIndex, staff.removed, roleConfig.id);
				tableHTML += cellHTML;
			});

			tableHTML += '</tr>';
		});

		tableHTML += '</tbody>';

		// Build footer totals if needed
		if (roleData.staff_counts.length > 0) {
			tableHTML += '<tfoot><tr>';

			roleConfig.columns.forEach(column => {
				if (column.calculated || column.type === COLUMN_TYPES.RUN_COUNT) {
					if (column.type === COLUMN_TYPES.RUN_COUNT && hasRuns) {
						runs.forEach(() => {
							tableHTML += '<td class="run text-center"><input type="text" value="0" readonly></td>';
						});
						if (hasRuns) tableHTML += '<td></td>'; // Empty cell for add run button
					} else if (column.calculated) {
						// Add text-center class for Presser and Woolhandler Day Total column
						const isPresser = roleConfig.id === 'presser';
						const isWoolhandler = roleConfig.id === 'woolhandler';
						const shouldCenter = (isPresser && column.type === COLUMN_TYPES.DAY_TALLY) ||
							(isWoolhandler && column.type === COLUMN_TYPES.DAY_TALLY);
						const centerClass = shouldCenter ? ' text-center' : '';

						tableHTML += `<td class="${centerClass}"><input type="text" readonly value="0" class="${centerClass}"></td>`;
					} else {
						tableHTML += '<td></td>';
					}
				} else if (column.type === COLUMN_TYPES.STAFF_NAME) {
					tableHTML += '<td>TOTALS</td>';
				} else {
					// Add text-center class for other Presser and Woolhandler columns in footer
					const isPresser = roleConfig.id === 'presser';
					const isWoolhandler = roleConfig.id === 'woolhandler';
					const shouldCenter = (isPresser && (
						column.type === 'total_runs' ||
						column.type === 'total_bales' ||
						column.type === 'total_kilo'
					)) || (isWoolhandler && (
						column.type === 'total_runs'
					));
					const centerClass = shouldCenter ? ' text-center' : '';

					tableHTML += `<td class="${centerClass}"></td>`;
				}
			});

			tableHTML += '</tr></tfoot>';
		}

		tableHTML += '</table>';
		tableHTML += '</div>'; // Close table-responsive div
		return tableHTML;
	}

	/**
	 * Create sectioned table for Shearing & Crutching role
	 */
	createSectionedTable(roleData, roleConfig, dayIndex) {
		const runs = roleData.runs || [];
		let tableHTML = '';

		// Create a table for each section (SHEARING and CRUTCHING)
		roleConfig.sections.forEach((section, sectionIndex) => {
			tableHTML += '<div class="table-responsive">';
			tableHTML += '<table class="job-tallies__table table">';
			tableHTML += '<thead><tr>';

			// Build headers - show the section name as the header
			tableHTML += `<th>${section}</th>`;

			// Run columns
			runs.forEach(run => {
				let selectOptions = '';
				if (roleConfig.hasRunNotes) {
					// Get section-specific notes or fall back to general notes
					const notes = (run.section_notes && run.section_notes[section]) || run.notes || '';

					if (section === 'CRUTCHING') {
						// Default to 'B/h' if no notes
						const defaultValue = notes || 'B/h';

						// If notes are empty, save the default value to the data structure
						if (!notes) {
							if (!run.section_notes) {
								run.section_notes = {};
							}
							run.section_notes[section] = defaultValue;
						}

						// Crutching-specific options
						selectOptions = `<br><select data-run-id="${run.id}" data-day-index="${dayIndex}" data-section="${section}" class="run-select">
							<option value="B/h" ${defaultValue === 'B/h' ? 'selected' : ''}>B/h</option>
							<option value="B/h wig" ${defaultValue === 'B/h wig' ? 'selected' : ''}>B/h wig</option>
							<option value="B/h wig ring" ${defaultValue === 'B/h wig ring' ? 'selected' : ''}>B/h wig ring</option>
							<option value="Big B/H" ${defaultValue === 'Big B/H' ? 'selected' : ''}>Big B/H</option>
							<option value="Full crt" ${defaultValue === 'Full crt' ? 'selected' : ''}>Full crt</option>
							<option value="Full crt wig" ${defaultValue === 'Full crt wig' ? 'selected' : ''}>Full crt wig</option>
							<option value="Full crt wig ring" ${defaultValue === 'Full crt wig ring' ? 'selected' : ''}>Full crt wig ring</option>
						</select>`;
					} else {
						// Default to 'Flock' if no notes
						const defaultValue = notes || 'Flock';

						// If notes are empty, save the default value to the data structure
						if (!notes) {
							if (!run.section_notes) {
								run.section_notes = {};
							}
							run.section_notes[section] = defaultValue;
						}

						// Shearing (default) options
						selectOptions = `<br><select data-run-id="${run.id}" data-day-index="${dayIndex}" data-section="${section}" class="run-select">
							<option value="Flock" ${defaultValue === 'Flock' ? 'selected' : ''}>Flock</option>
							<option value="Stud" ${defaultValue === 'Stud' ? 'selected' : ''}>Stud</option>
							<option value="Ram" ${defaultValue === 'Ram' ? 'selected' : ''}>Ram</option>
							<option value="Other" ${defaultValue === 'Other' ? 'selected' : ''}>Other</option>
						</select>`;
					}
				}

				tableHTML += `<th class="run-col text-center" scope="col">
					${run.name}
					${selectOptions}
				</th>`;
			});

			if (roleConfig.allowMultipleRuns) {
				tableHTML += `<th scope="col" class="add-run-col"><button type="button" class="add-run btn" data-day-index="${dayIndex}" data-section="${section}">+ ADD RUN</button></th>`;
			}

			tableHTML += '<th class="day-total-col" scope="col">DAY TOTAL</th>';
			tableHTML += '<th class="mess-col" scope="col">Mess</th>';
			tableHTML += '<th class="evos-col" scope="col">Evo\'s</th>';
			tableHTML += '<th class="travel-col" scope="col">Travel</th>';
			tableHTML += '</tr></thead><tbody>';

			// Get staff for this section - filter by original role
			const sectionStaff = roleData.staff_counts.filter(staff => {
				// Show staff based on their original role allocation
				if (section === 'SHEARING') {
					return staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers';
				} else if (section === 'CRUTCHING') {
					// Show both Crutchers AND Shearers in crutching section
					return staff.originalRole === 'Crutcher' || staff.originalRole === 'Crutchers' || staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers';
				}
				return false; // Default: don't show in unknown sections
			});

			// Build rows - only if there are staff
			if (sectionStaff.length === 0) {
				// Empty row when no staff - create individual cells for each column
				tableHTML += '<tr>';
				tableHTML += '<td>&nbsp;</td>'; // Section/staff name column

				// Run columns
				runs.forEach(() => {
					tableHTML += '<td>&nbsp;</td>';
				});

				if (roleConfig.allowMultipleRuns) {
					tableHTML += '<td>&nbsp;</td>'; // Add run button column
				}

				tableHTML += '<td>&nbsp;</td>'; // Day total column
				tableHTML += '<td>&nbsp;</td>'; // Mess column
				tableHTML += '<td>&nbsp;</td>'; // Evo's column
				tableHTML += '<td>&nbsp;</td>'; // Travel column
				tableHTML += '</tr>';
			} else {
				sectionStaff.forEach((staff) => {
					// Find the original staff index in the full staff_counts array
					const staffIndex = roleData.staff_counts.findIndex(s => s.staff_id === staff.staff_id);
					const removedClass = staff.removed ? ' class="staff-removed"' : '';
					tableHTML += `<tr${removedClass}>`;
					// Display the actual staff name in the body
					const removedIndicator = staff.removed ? ' <span class="text-muted">(removed)</span>' : '';
					const staffNameCell = `<span class="staff-name-text">${staff.staff_name}${removedIndicator}</span>`;
					tableHTML += `<td>${staffNameCell}</td>`;

					// Run inputs - use section-specific runs
					const sectionRuns = section === 'SHEARING' ? staff.shearing_runs : staff.crutching_runs;

					// Always create cells for all runs defined in the header
					runs.forEach((run, runIndex) => {
						let runValue = '0';
						// Check if this staff has a value for this specific run
						if (sectionRuns && sectionRuns[runIndex]) {
							runValue = sectionRuns[runIndex].count || '0';
						}

						tableHTML += `<td class="run text-center">
							<input type="number" value="${runValue}"
								data-day-index="${dayIndex}"
								data-section="${section}"
								data-staff-index="${staffIndex}"
								data-run-index="${runIndex}">
						</td>`;
					});

					if (roleConfig.allowMultipleRuns) {
						tableHTML += '<td></td>'; // Empty cell for add run button column
					}

					// Day total - calculate section-specific total
					let dayTotal = 0;
					const sectionRunsForTotal = section === 'SHEARING' ? staff.shearing_runs : staff.crutching_runs;
					if (sectionRunsForTotal) {
						sectionRunsForTotal.forEach(run => {
							dayTotal += parseFloat(run.count) || 0;
						});
					}
					tableHTML += `<td><input type="text" readonly value="${dayTotal}"></td>`;

					// Mess number input - use section-specific values
					const messValue = section === 'SHEARING' ? (staff.shearing_mess || 0) : (staff.crutching_mess || 0);
					tableHTML += `<td class="text-center"><input type="number" value="${messValue}" min="0"
						data-day-index="${dayIndex}"
						data-section="${section}"
						data-staff-index="${staffIndex}"
						data-field="mess"></td>`;

					// Evo's number input - use section-specific values
					const evosValue = section === 'SHEARING' ? (staff.shearing_evos || 0) : (staff.crutching_evos || 0);
					tableHTML += `<td class="text-center"><input type="number" value="${evosValue}" min="0"
						data-day-index="${dayIndex}"
						data-section="${section}"
						data-staff-index="${staffIndex}"
						data-field="evos"></td>`;

					// Travel number input - use section-specific values
					const travelValue = section === 'SHEARING' ? (staff.shearing_travel || 0) : (staff.crutching_travel || 0);
					tableHTML += `<td class="text-center"><input type="number" value="${travelValue}" min="0"
						data-day-index="${dayIndex}"
						data-section="${section}"
						data-staff-index="${staffIndex}"
						data-field="travel"></td>`;

					tableHTML += '</tr>';
				});
			}

			tableHTML += '</tbody>';

			// Footer totals
			tableHTML += '<tfoot><tr>';
			tableHTML += '<td>Totals</td>';

			// Run totals
			runs.forEach(() => {
				tableHTML += '<td class="run text-center"><input type="text" value="0" readonly></td>';
			});

			if (roleConfig.allowMultipleRuns) {
				tableHTML += '<td></td>';
			}

			tableHTML += '<td><input type="text" readonly value="0"></td>'; // Day total
			tableHTML += '<td></td>'; // Mess
			tableHTML += '<td></td>'; // Evo's
			tableHTML += '<td></td>'; // Travel

			tableHTML += '</tr></tfoot></table>';
			tableHTML += '</div>'; // Close table-responsive div

			// Add spacing between sections
			if (sectionIndex < roleConfig.sections.length - 1) {
				tableHTML += '<br>';
			}
		});

		// Sync default values to hidden input after setting them
		this.syncToHiddenInput();

		return tableHTML;
	}

	/**
	 * Create individual table cell based on column configuration
	 */
	createTableCell(column, staff, staffIndex, dayIndex, isRemoved = false) {
		let cellHTML = '<td';

		if (column.width) {
			cellHTML += ` style="width: ${column.width}"`;
		}

		cellHTML += '>';

		switch (column.type) {
			case COLUMN_TYPES.STAFF_NUMBER:
				cellHTML += `${staffIndex + 1}`;
				break;

			case COLUMN_TYPES.STAFF_NAME: {
				const removedIndicator = isRemoved ? ' <span class="text-muted">(removed)</span>' : '';
				cellHTML += `<span class="staff-name-text">${staff.staff_name}${removedIndicator}</span>`;
				break;
			}

			case COLUMN_TYPES.RUN_COUNT:
				// Handle runs
				if (staff.runs && Array.isArray(staff.runs)) {
					staff.runs.forEach((run, runIndex) => {
						cellHTML += `</td><td class="run text-center">
                            <input type="number" value="${run.count}" 
                                data-day-index="${dayIndex}" 
                                data-staff-index="${staffIndex}" 
                                data-run-index="${runIndex}">
                        `;
					});
					cellHTML += `</td><td>`;
				}
				break;

			case COLUMN_TYPES.DAY_TALLY: {
				const dayTally = staff.day_tally || 0;
				cellHTML += `<input type="text" readonly value="${dayTally}" class="text-center">`;
				break;
			}

			case COLUMN_TYPES.TRAVEL:
				cellHTML += `<input type="number" value="${staff.travel || 0}" min="0"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="travel">`;
				break;

			case COLUMN_TYPES.HOURS:
				cellHTML += `<input type="number" step="${column.step || 1}" value="${staff.hours || '0'}"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}">`;
				break;

			case COLUMN_TYPES.RATE:
				cellHTML += `${column.prefix || ''}<input type="number" step="${column.step || 1}" value="${staff.rate || '0'}"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}">`;
				break;

			case COLUMN_TYPES.AMOUNT: {
				const amount = staff.amount || '0.00';
				cellHTML += `${column.prefix || ''}<input type="text" readonly value="${amount}">`;
				break;
			}

			case COLUMN_TYPES.NOTES:
				cellHTML += `<input type="text" value="${staff.notes || ''}"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}">`;
				break;

			case 'total_runs':
				cellHTML += `<input type="number" value="${staff.total_runs || '0'}"
                    class="text-center"
                    style="border: none; -webkit-appearance: none; -moz-appearance: textfield;"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}"
                    data-field="total_runs">`;
				break;

			case 'total_bales':
				cellHTML += `<input type="number" value="${staff.total_bales || '0'}"
                    class="text-center"
                    style="border: none; -webkit-appearance: none; -moz-appearance: textfield;"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}"
                    data-field="total_bales">`;
				break;

			case 'total_kilo':
				cellHTML += `<input type="number" value="${staff.total_kilo || '0'}"
                    class="text-center"
                    style="border: none; -webkit-appearance: none; -moz-appearance: textfield;"
                    data-day-index="${dayIndex}" 
                    data-staff-index="${staffIndex}"
                    data-field="total_kilo">`;
				break;

			case 'total_fleeces':
				cellHTML += `<input type="number" value="${staff.total_fleeces || '0'}"
                    class="text-center"
                    style="border: none; -webkit-appearance: none; -moz-appearance: textfield;"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="total_fleeces">`;
				break;

			case 'payment_type': {
				const paymentTypeOptions = column.options || [];
				let selectHTML = `<select class="form-select form-select-sm"
                    style="border: none; font-size: 12px; padding: 4px 16px 4px 8px; background-position: right 2px center; background-size: 12px;"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="payment_type">`;
				paymentTypeOptions.forEach(opt => {
					const selected = staff.payment_type === opt.value ? 'selected' : '';
					selectHTML += `<option value="${opt.value}" ${selected}>${opt.label}</option>`;
				});
				selectHTML += '</select>';
				cellHTML += selectHTML;
				break;
			}

			case 'count':
				cellHTML += `<input type="number" value="${staff.count || '0'}"
                    class="text-center"
                    style="border: none; -webkit-appearance: none; -moz-appearance: textfield;"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="count">`;
				break;

			case 'mess':
				cellHTML += `<input type="number" value="${staff.mess || 0}" min="0"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="mess">`;
				break;

			case 'evos':
				cellHTML += `<input type="number" value="${staff.evos || 0}" min="0"
                    data-day-index="${dayIndex}"
                    data-staff-index="${staffIndex}"
                    data-field="evos">`;
				break;

			default:
				cellHTML += '';
		}

		cellHTML += '</td>';
		return cellHTML;
	}

	/**
	 * Bind table events for current role
	 */
	bindTableEvents(roleId) {
		const roleConfig = getRoleById(roleId);
		if (!roleConfig) return;

		// Bind input events
		document.querySelectorAll('.job-tallies__table input').forEach(input => {
			input.addEventListener('input', (e) => this.handleInputChange(e, roleId));
		});

		// Bind select events for run notes
		document.querySelectorAll('.job-tallies__table .run-select').forEach(select => {
			select.addEventListener('change', (e) => this.handleSelectChange(e, roleId));
		});

		// Bind select events for field selects (like payment_type)
		document.querySelectorAll('.job-tallies__table select[data-field]').forEach(select => {
			select.addEventListener('change', (e) => this.handleInputChange(e, roleId));
		});

		// Bind add run buttons
		document.querySelectorAll('.add-run').forEach(btn => {
			btn.addEventListener('click', (e) => {
				const dayIndex = parseInt(e.target.dataset.dayIndex);
				this.addRun(dayIndex, roleId);
			});
		});

	}

	/**
	 * Handle select changes for run notes
	 */
	handleSelectChange(event, roleId) {
		const select = event.target;
		const dayIndex = parseInt(select.dataset.dayIndex);
		const runId = select.dataset.runId;
		const section = select.dataset.section;

		if (isNaN(dayIndex) || !runId) return;

		const day = this.talliesData.tally_data[dayIndex];
		const roleData = day.roles.find(r => r.role_id === roleId);

		if (!roleData || !roleData.runs) return;

		// Find the run and update its notes
		const run = roleData.runs.find(r => r.id === runId);
		if (run) {
			// If role has sections, store notes per section
			if (section) {
				if (!run.section_notes) {
					run.section_notes = {};
				}
				run.section_notes[section] = select.value;
			} else {
				run.notes = select.value;
			}
			this.syncToHiddenInput();

			// Update summary tables if this is shearing_crutching role
			if (roleId === 'shearing_crutching') {
				this.renderSummaryTable();
			}
		}
	}

	/**
	 * Handle input changes
	 */
	handleInputChange(event, roleId) {
		const input = event.target;
		const dayIndex = parseInt(input.dataset.dayIndex);
		const staffIndex = parseInt(input.dataset.staffIndex);
		const runIndex = input.dataset.runIndex ? parseInt(input.dataset.runIndex) : null;
		const fieldName = input.dataset.field;

		if (isNaN(dayIndex) || isNaN(staffIndex)) return;

		const day = this.talliesData.tally_data[dayIndex];
		const roleData = day.roles.find(r => r.role_id === roleId);
		const staff = roleData.staff_counts[staffIndex];

		if (!staff) return;

		// Update data based on input type
		if (fieldName === 'mess') {
			const section = input.dataset.section;
			if (section === 'SHEARING') {
				if (!staff.shearing_mess) staff.shearing_mess = 0;
				staff.shearing_mess = parseFloat(input.value) || 0;
			} else if (section === 'CRUTCHING') {
				if (!staff.crutching_mess) staff.crutching_mess = 0;
				staff.crutching_mess = parseFloat(input.value) || 0;
			} else {
				// For non-sectioned roles, use the original field
				staff.mess = parseFloat(input.value) || 0;
			}
		} else if (fieldName === 'evos') {
			const section = input.dataset.section;
			if (section === 'SHEARING') {
				if (!staff.shearing_evos) staff.shearing_evos = 0;
				staff.shearing_evos = parseFloat(input.value) || 0;
			} else if (section === 'CRUTCHING') {
				if (!staff.crutching_evos) staff.crutching_evos = 0;
				staff.crutching_evos = parseFloat(input.value) || 0;
			} else {
				// For non-sectioned roles, use the original field
				staff.evos = parseFloat(input.value) || 0;
			}
		} else if (fieldName === 'travel') {
			const section = input.dataset.section;
			if (section === 'SHEARING') {
				if (!staff.shearing_travel) staff.shearing_travel = 0;
				staff.shearing_travel = parseFloat(input.value) || 0;
			} else if (section === 'CRUTCHING') {
				if (!staff.crutching_travel) staff.crutching_travel = 0;
				staff.crutching_travel = parseFloat(input.value) || 0;
			} else {
				// For non-sectioned roles, use the original field
				staff.travel = parseFloat(input.value) || 0;
			}
		} else if (runIndex !== null) {
			// Update section-specific runs
			const section = input.dataset.section;
			if (section === 'SHEARING') {
				if (!staff.shearing_runs) staff.shearing_runs = [];
				if (!staff.shearing_runs[runIndex]) {
					staff.shearing_runs[runIndex] = { run: (runIndex + 1).toString(), count: '0' };
				}
				staff.shearing_runs[runIndex].count = input.value;
			} else if (section === 'CRUTCHING') {
				if (!staff.crutching_runs) staff.crutching_runs = [];
				if (!staff.crutching_runs[runIndex]) {
					staff.crutching_runs[runIndex] = { run: (runIndex + 1).toString(), count: '0' };
				}
				staff.crutching_runs[runIndex].count = input.value;
			}
			// Also update the legacy runs array for backward compatibility
			if (!staff.runs) staff.runs = [];
			if (!staff.runs[runIndex]) {
				staff.runs[runIndex] = { run: (runIndex + 1).toString(), count: '0' };
			}
			staff.runs[runIndex].count = input.value;
		} else if (fieldName) {
			// Handle presser-specific fields and other fields by name
			switch (fieldName) {
				case 'total_runs':
					staff.total_runs = parseFloat(input.value) || 0;
					break;
				case 'total_bales':
					staff.total_bales = parseFloat(input.value) || 0;
					break;
				case 'total_kilo':
					staff.total_kilo = parseFloat(input.value) || 0;
					break;
				case 'total_fleeces':
					staff.total_fleeces = parseFloat(input.value) || 0;
					break;
				case 'payment_type':
					staff.payment_type = input.value;
					break;
				case 'count':
					staff.count = parseFloat(input.value) || 0;
					break;
			}
		} else if (input.dataset.dayIndex && input.dataset.staffIndex) {
			// Determine field type from parent cell
			const cell = input.closest('td');
			const column = this.getColumnFromCell(cell, getRoleById(roleId));

			if (column) {
				switch (column.type) {
					case COLUMN_TYPES.HOURS:
						staff.hours = input.value;
						break;
					case COLUMN_TYPES.RATE:
						staff.rate = input.value;
						break;
					case COLUMN_TYPES.NOTES:
						staff.notes = input.value;
						break;
				}
			}
		}

		// Recalculate values
		const updatedStaff = calculateStaffValues(roleId, staff);
		Object.assign(staff, updatedStaff);

		this.updateRowCalculations(input.closest('tr'), roleId);
		this.updateColumnTotals(input.closest('table'), roleId);
		this.syncToHiddenInput();
		// Always update all summary tables when data changes
		this.renderAllSummaryTables();
	}

	/**
	 * Get column configuration from table cell
	 */
	getColumnFromCell(cell, roleConfig) {
		const cellIndex = Array.from(cell.parentNode.children).indexOf(cell);

		// This is a simplified mapping - you might need more sophisticated logic
		// based on your actual table structure
		return roleConfig.columns[cellIndex] || null;
	}

	/**
	 * Add a new run to a day for shearing role
	 */
	addRun(dayIndex, roleId) {
		const roleConfig = getRoleById(roleId);
		if (!roleConfig || !roleConfig.allowMultipleRuns) return;

		const day = this.talliesData.tally_data[dayIndex];
		const roleData = day.roles.find(r => r.role_id === roleId);

		if (!roleData.runs) roleData.runs = [];

		const runCount = roleData.runs.length + 1;
		const newRun = {
			id: runCount.toString(),
			name: `Run ${runCount}`,
			notes: ''
		};

		roleData.runs.push(newRun);

		// Add new run entry to all staff - for both sections if needed
		roleData.staff_counts.forEach(staff => {
			// Always maintain legacy runs array
			if (!staff.runs) staff.runs = [];
			staff.runs.push({
				run: newRun.id,
				count: '0'
			});

			// Add to shearing runs if staff is in shearing section
			if (staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers') {
				if (!staff.shearing_runs) staff.shearing_runs = [];
				staff.shearing_runs.push({
					run: newRun.id,
					count: '0'
				});
			}

			// Add to crutching runs if staff is in crutching section
			if (staff.originalRole === 'Crutcher' || staff.originalRole === 'Crutchers' || staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers') {
				if (!staff.crutching_runs) staff.crutching_runs = [];
				staff.crutching_runs.push({
					run: newRun.id,
					count: '0'
				});
			}
		});

		this.renderTablesForRole(roleId);
		this.syncToHiddenInput();
	}

	/**
	 * Update row calculations for a specific row
	 */
	updateRowCalculations(row, roleId) {
		if (!row) return;

		const roleConfig = getRoleById(roleId);
		if (!roleConfig) return;

		// Calculate day tally for this row - exclude Mess, Evos, Travel inputs
		const runInputs = row.querySelectorAll('input[type="number"]:not([data-field="mess"]):not([data-field="evos"]):not([data-field="travel"])');
		let dayTotal = 0;

		runInputs.forEach(input => {
			const value = parseFloat(input.value) || 0;
			dayTotal += value;
		});

		// Update day total input
		const dayTotalInput = row.querySelector('input[readonly]');
		if (dayTotalInput) {
			dayTotalInput.value = dayTotal;
		}
	}

	/**
	 * Update column totals for a table
	 */
	updateColumnTotals(table, roleId) {
		if (!table) return;

		const roleConfig = getRoleById(roleId);
		if (!roleConfig) return;

		const tbody = table.querySelector('tbody');
		const tfoot = table.querySelector('tfoot');

		if (!tbody || !tfoot) return;

		const bodyRows = tbody.querySelectorAll('tr');
		const footerRow = tfoot.querySelector('tr');

		if (!footerRow) return;

		// Handle Presser role specifically
		if (roleConfig.id === 'presser') {
			this.updatePresserColumnTotals(table, bodyRows, footerRow);
			return;
		}

		// Handle Woolhandler role specifically
		if (roleConfig.id === 'woolhandler') {
			this.updateWoolhandlerColumnTotals(table, bodyRows, footerRow);
			return;
		}

		// Handle Classer role specifically
		if (roleConfig.id === 'classer') {
			this.updateClasserColumnTotals(table, bodyRows, footerRow);
			return;
		}

		// Get all run columns (number inputs in body)
		const runColumnCount = bodyRows.length > 0 ?
			bodyRows[0].querySelectorAll('input[type="number"]').length : 0;

		// Calculate totals for each run column
		for (let colIndex = 0; colIndex < runColumnCount; colIndex++) {
			let columnTotal = 0;

			bodyRows.forEach(row => {
				const runInputs = row.querySelectorAll('input[type="number"]');
				if (runInputs[colIndex]) {
					const value = parseFloat(runInputs[colIndex].value) || 0;
					columnTotal += value;
				}
			});

			// Update footer total for this column
			const footerRunInputs = footerRow.querySelectorAll('input[readonly]');
			if (footerRunInputs[colIndex]) {
				footerRunInputs[colIndex].value = columnTotal;
			}
		}

		// Calculate day total column total
		let dayTotalSum = 0;
		bodyRows.forEach(row => {
			// Find the day total input - it's the readonly input that's not in a run column
			const allReadonlyInputs = row.querySelectorAll('input[readonly]');
			const dayTotalInput = allReadonlyInputs[allReadonlyInputs.length - 1]; // Last readonly input should be day total
			if (dayTotalInput) {
				const value = parseFloat(dayTotalInput.value) || 0;
				dayTotalSum += value;
			}
		});

		// Update day total footer - find the last readonly input in footer that corresponds to day total
		const footerReadonlyInputs = footerRow.querySelectorAll('input[readonly]');
		const dayTotalFooterInput = footerReadonlyInputs[footerReadonlyInputs.length - 1]; // Last readonly input
		if (dayTotalFooterInput) {
			dayTotalFooterInput.value = dayTotalSum;
		}
	}

	/**
	 * Update column totals specifically for Presser tables
	 */
	updatePresserColumnTotals(table, bodyRows, footerRow) {
		const totals = {
			totalRuns: 0,
			totalBales: 0,
			totalKilo: 0,
			dayTotal: 0
		};

		// Calculate totals from all body rows
		bodyRows.forEach(row => {
			// Find presser-specific inputs based on data-field attributes
			const totalRunsInput = row.querySelector('input[data-field="total_runs"]');
			const totalBalesInput = row.querySelector('input[data-field="total_bales"]');
			const totalKiloInput = row.querySelector('input[data-field="total_kilo"]');
			const dayTotalInput = row.querySelector('input[readonly]');

			if (totalRunsInput) {
				totals.totalRuns += parseFloat(totalRunsInput.value) || 0;
			}
			if (totalBalesInput) {
				totals.totalBales += parseFloat(totalBalesInput.value) || 0;
			}
			if (totalKiloInput) {
				totals.totalKilo += parseFloat(totalKiloInput.value) || 0;
			}
			if (dayTotalInput) {
				totals.dayTotal += parseFloat(dayTotalInput.value) || 0;
			}
		});

		// Update footer cells - need to find the correct cells by position
		// Based on the Presser table structure: Presser | Total Runs | Total Bales | Total Kilo | DAY TOTAL | Mess | Evo's
		const footerCells = footerRow.querySelectorAll('td');
		if (footerCells.length >= 5) {
			// Update the footer totals (skip first cell which is "TOTALS" label)
			if (footerCells[1]) footerCells[1].textContent = totals.totalRuns;
			if (footerCells[2]) footerCells[2].textContent = totals.totalBales;
			if (footerCells[3]) footerCells[3].textContent = totals.totalKilo;

			// Update Day Total - find the readonly input in the footer
			const dayTotalFooterInput = footerRow.querySelector('input[readonly]');
			if (dayTotalFooterInput) {
				dayTotalFooterInput.value = totals.dayTotal;
			}
		}
	}

	/**
	 * Update column totals specifically for Woolhandler tables
	 */
	updateWoolhandlerColumnTotals(table, bodyRows, footerRow) {
		const totals = {
			totalRuns: 0,
			dayTotal: 0
		};

		// Calculate totals from all body rows
		bodyRows.forEach(row => {
			// Find woolhandler-specific inputs based on data-field attributes
			const totalRunsInput = row.querySelector('input[data-field="total_runs"]');
			const dayTotalInput = row.querySelector('input[readonly]');

			if (totalRunsInput) {
				totals.totalRuns += parseFloat(totalRunsInput.value) || 0;
			}
			if (dayTotalInput) {
				totals.dayTotal += parseFloat(dayTotalInput.value) || 0;
			}
		});

		// Update footer cells - need to find the correct cells by position
		// Based on the Woolhandler table structure: Woolhandler | Total Runs | DAY TOTAL | Mess | Evo's
		const footerCells = footerRow.querySelectorAll('td');
		if (footerCells.length >= 3) {
			// Update the footer totals (skip first cell which is "TOTALS" label)
			if (footerCells[1]) footerCells[1].textContent = totals.totalRuns;

			// Update Day Total - find the readonly input in the footer
			const dayTotalFooterInput = footerRow.querySelector('input[readonly]');
			if (dayTotalFooterInput) {
				dayTotalFooterInput.value = totals.dayTotal;
			}
		}
	}

	/**
	 * Update column totals specifically for Classer tables
	 */
	updateClasserColumnTotals(table, bodyRows, footerRow) {
		const totals = {
			totalRuns: 0,
			totalFleeces: 0,
			dayTotal: 0
		};

		// Calculate totals from all body rows
		bodyRows.forEach(row => {
			// Find classer-specific inputs based on data-field attributes
			const totalRunsInput = row.querySelector('input[data-field="total_runs"]');
			const totalFleeceInput = row.querySelector('input[data-field="total_fleeces"]');
			const dayTotalInput = row.querySelector('input[readonly]');

			if (totalRunsInput) {
				totals.totalRuns += parseFloat(totalRunsInput.value) || 0;
			}
			if (totalFleeceInput) {
				totals.totalFleeces += parseFloat(totalFleeceInput.value) || 0;
			}
			if (dayTotalInput) {
				totals.dayTotal += parseFloat(dayTotalInput.value) || 0;
			}
		});

		// Update footer cells - need to find the correct cells by position
		// Based on the Classer table structure: Classer | Total Runs | Total Fleeces | DAY TOTAL | Mess | Evo's
		const footerCells = footerRow.querySelectorAll('td');
		if (footerCells.length >= 4) {
			// Update the footer totals (skip first cell which is "TOTALS" label)
			if (footerCells[1]) footerCells[1].textContent = totals.totalRuns;
			if (footerCells[2]) footerCells[2].textContent = totals.totalFleeces;

			// Update Day Total - find the readonly input in the footer
			const dayTotalFooterInput = footerRow.querySelector('input[readonly]');
			if (dayTotalFooterInput) {
				dayTotalFooterInput.value = totals.dayTotal;
			}
		}
	}

	/**
	 * Update all calculations
	 */
	updateAllCalculations() {
		// Recalculate all values for current role
		if (!this.currentRoleId) return;

		this.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === this.currentRoleId);
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const updated = calculateStaffValues(this.currentRoleId, staff);
				Object.assign(staff, updated);
			});
		});

		this.syncToHiddenInput();
	}

	/**
	 * Render all summary tables for roles with staff only
	 */
	renderAllSummaryTables() {
		// Guard against null talliesData
		if (!this.talliesData || !this.talliesData.tally_data) {
			return;
		}

		// Only render summary tables for roles that have staff
		if (this.roleHasStaff('shearing_crutching')) {
			this.showElement('#shearingTally');
			this.showElement('#crutchingTally');
			this.renderShearingCrutchingSummaryTables();
		} else {
			this.hideElement('#shearingTally');
			this.hideElement('#crutchingTally');
		}

		if (this.roleHasStaff('presser')) {
			this.showElement('#presserTally');
			this.renderPresserSummaryTable();
		} else {
			this.hideElement('#presserTally');
		}

		if (this.roleHasStaff('woolhandler')) {
			this.showElement('#woolhandlerTally');
			this.renderWoolhandlerSummaryTable();
		} else {
			this.hideElement('#woolhandlerTally');
		}

		if (this.roleHasStaff('classer')) {
			this.showElement('#classerTally');
			this.renderClasserSummaryTable();
		} else {
			this.hideElement('#classerTally');
		}

		if (this.roleHasStaff('sheepo')) {
			this.showElement('#sheepoTally');
			this.renderSheepoSummaryTable();
		} else {
			this.hideElement('#sheepoTally');
		}

		if (this.roleHasStaff('cook')) {
			this.showElement('#cookTally');
			this.renderCookSummaryTable();
		} else {
			this.hideElement('#cookTally');
		}
	}

	/**
	 * Render all role summary tables
	 */
	renderSummaryTable() {
		// Handle shearing_crutching role specially to populate separate tables
		if (this.currentRoleId === 'shearing_crutching') {
			this.renderShearingCrutchingSummaryTables();
		} else if (this.currentRoleId === 'presser') {
			this.renderPresserSummaryTable();
		} else if (this.currentRoleId === 'woolhandler') {
			this.renderWoolhandlerSummaryTable();
		}
		// For other roles, do nothing - leave the fixed Shearing/Crutching/Presser/Woolhandler summary tables unchanged
	}

	/**
	 * Render separate summary tables for Shearing and Crutching
	 */
	renderShearingCrutchingSummaryTables() {
		// Get summary data for shearing/crutching role with section breakdown
		const shearingSummaryData = this.calculateSummaryDataBySection('SHEARING');
		const crutchingSummaryData = this.calculateSummaryDataBySection('CRUTCHING');

		// Populate Shearing tally table
		this.populateShearingCrutchingSummaryTable('shearingTally', shearingSummaryData);

		// Populate Crutching tally table
		this.populateShearingCrutchingSummaryTable('crutchingTally', crutchingSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Populate a specific shearing/crutching summary table
	 */
	populateShearingCrutchingSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Get section type from tableId
		const section = tableId === 'shearingTally' ? 'SHEARING' : 'CRUTCHING';

		// Calculate totals for each type - initialize based on section
		let totals;
		if (section === 'CRUTCHING') {
			totals = {
				bh: 0,
				bh_wig: 0,
				bh_wig_ring: 0,
				big_bh: 0,
				full_crt: 0,
				full_crt_wig: 0,
				full_crt_wig_ring: 0,
				total: 0
			};
		} else {
			totals = {
				flock: 0,
				stud: 0,
				ram: 0,
				other: 0,
				total: 0
			};
		}

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Calculate totals for this staff member
			let staffTotals, staffTotal;

			if (section === 'CRUTCHING') {
				// Crutching-specific totals
				staffTotals = {
					'bh': staff.crutchingTypes?.['B/h'] || 0,
					'bh_wig': staff.crutchingTypes?.['B/h wig'] || 0,
					'bh_wig_ring': staff.crutchingTypes?.['B/h wig ring'] || 0,
					'big_bh': staff.crutchingTypes?.['Big B/H'] || 0,
					'full_crt': staff.crutchingTypes?.['Full crt'] || 0,
					'full_crt_wig': staff.crutchingTypes?.['Full crt wig'] || 0,
					'full_crt_wig_ring': staff.crutchingTypes?.['Full crt wig ring'] || 0
				};
				staffTotal = Object.values(staffTotals).reduce((sum, val) => sum + val, 0);

				// Add to grand totals for crutching
				totals.bh += staffTotals.bh;
				totals.bh_wig += staffTotals.bh_wig;
				totals.bh_wig_ring += staffTotals.bh_wig_ring;
				totals.big_bh += staffTotals.big_bh;
				totals.full_crt += staffTotals.full_crt;
				totals.full_crt_wig += staffTotals.full_crt_wig;
				totals.full_crt_wig_ring += staffTotals.full_crt_wig_ring;
				totals.total += staffTotal;
			} else {
				// Shearing totals (unchanged)
				staffTotals = {
					flock: staff.sheepTypes?.flock || 0,
					stud: staff.sheepTypes?.stud || 0,
					ram: staff.sheepTypes?.ram || 0,
					other: staff.sheepTypes?.other || 0
				};
				staffTotal = staffTotals.flock + staffTotals.stud + staffTotals.ram + staffTotals.other;

				// Add to grand totals for shearing
				totals.flock += staffTotals.flock;
				totals.stud += staffTotals.stud;
				totals.ram += staffTotals.ram;
				totals.other += staffTotals.other;
				totals.total += staffTotal;
			}

			// Calculate mess and evo counts for this staff member
			const mesEvoData = this.calculateMessEvoCounts(staffId, section);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			if (section === 'CRUTCHING') {
				row.innerHTML = `
					<td>
						<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
							<i class="far fa-angle-right"></i>
						</span>
						<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
					</td>
					<td class="text-end">${staffTotals.bh}</td>
					<td class="text-end">${staffTotals.bh_wig}</td>
					<td class="text-end">${staffTotals.bh_wig_ring}</td>
					<td class="text-end">${staffTotals.big_bh}</td>
					<td class="text-end">${staffTotals.full_crt}</td>
					<td class="text-end">${staffTotals.full_crt_wig}</td>
					<td class="text-end">${staffTotals.full_crt_wig_ring}</td>
					<td class="text-end">${staffTotal}</td>
				`;
			} else {
				row.innerHTML = `
					<td>
						<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
							<i class="far fa-angle-right"></i>
						</span>
						<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
					</td>
					<td class="text-end">${staffTotals.flock}</td>
					<td class="text-end">${staffTotals.stud}</td>
					<td class="text-end">${staffTotals.ram}</td>
					<td class="text-end">${staffTotals.other}</td>
					<td class="text-end">${staffTotal}</td>
				`;
			}
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			const colspanCount = section === 'CRUTCHING' ? 9 : 6;
			collapseRow.innerHTML = `
				<td colspan="${colspanCount}" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${mesEvoData.mess} | Evo: ${mesEvoData.evo} | Travel: ${mesEvoData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (section === 'CRUTCHING' && cells.length >= 9) {
					cells[1].textContent = totals.bh;
					cells[2].textContent = totals.bh_wig;
					cells[3].textContent = totals.bh_wig_ring;
					cells[4].textContent = totals.big_bh;
					cells[5].textContent = totals.full_crt;
					cells[6].textContent = totals.full_crt_wig;
					cells[7].textContent = totals.full_crt_wig_ring;
					cells[8].textContent = totals.total;
				} else if (cells.length >= 6) {
					cells[1].textContent = totals.flock;
					cells[2].textContent = totals.stud;
					cells[3].textContent = totals.ram;
					cells[4].textContent = totals.other;
					cells[5].textContent = totals.total;
				}
			}
		}
	}

	/**
	 * Calculate summary data by section (SHEARING or CRUTCHING)
	 */
	calculateSummaryDataBySection(section) {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'shearing_crutching');
			if (!roleData) return;

			// Filter staff by section based on original role
			const sectionStaff = roleData.staff_counts.filter(staff => {
				if (section === 'SHEARING') {
					return staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers';
				} else if (section === 'CRUTCHING') {
					// Show both Crutchers AND Shearers in crutching section
					return staff.originalRole === 'Crutcher' || staff.originalRole === 'Crutchers' || staff.originalRole === 'Shearing' || staff.originalRole === 'Shearers';
				}
				return false;
			});

			sectionStaff.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					if (section === 'CRUTCHING') {
						summaryData[id] = {
							name: staff.staff_name,
							crutchingTypes: {
								'B/h': 0,
								'B/h wig': 0,
								'B/h wig ring': 0,
								'Big B/H': 0,
								'Full crt': 0,
								'Full crt wig': 0,
								'Full crt wig ring': 0
							}
						};
					} else {
						summaryData[id] = {
							name: staff.staff_name,
							sheepTypes: {
								flock: 0,
								stud: 0,
								ram: 0,
								other: 0
							}
						};
					}
				}

				// Sum up run counts by type based on run notes - use section-specific runs
				const sectionRuns = section === 'SHEARING' ? staff.shearing_runs : staff.crutching_runs;
				if (sectionRuns && Array.isArray(sectionRuns) && roleData.runs) {
					sectionRuns.forEach(staffRun => {
						// Find the corresponding run configuration to get notes
						const runConfig = roleData.runs.find(r => r.id === staffRun.run);
						if (runConfig) {
							// Get section-specific notes or fall back to general notes
							const notes = (runConfig.section_notes && runConfig.section_notes[section]) || runConfig.notes;

							if (notes) {
								const count = parseFloat(staffRun.count) || 0;

								if (section === 'CRUTCHING') {
									// Handle crutching types
									const crutchType = notes;
									if (Object.prototype.hasOwnProperty.call(summaryData[id].crutchingTypes, crutchType)) {
										summaryData[id].crutchingTypes[crutchType] += count;
									}
								} else {
									// Handle shearing sheep types
									const sheepType = notes.toLowerCase();
									switch (sheepType) {
										case 'flock':
											summaryData[id].sheepTypes.flock += count;
											break;
										case 'stud':
											summaryData[id].sheepTypes.stud += count;
											break;
										case 'ram':
											summaryData[id].sheepTypes.ram += count;
											break;
										case 'other':
											summaryData[id].sheepTypes.other += count;
											break;
									}
								}
							}
						}
					});
				}
			});
		});

		return summaryData;
	}

	/**
	 * Create summary table for a specific role
	 */
	createRoleSummaryTable(role, summaryData) {
		const tableContainer = document.createElement('div');
		tableContainer.className = 'role-summary-table mb-3';

		// Create table-responsive wrapper
		const tableResponsive = document.createElement('div');
		tableResponsive.className = 'table-responsive';

		// Table
		const table = document.createElement('table');
		table.className = 'job-tallies__summary-table table table-sm';

		// Table header
		const thead = document.createElement('thead');
		const headerRow = document.createElement('tr');
		headerRow.innerHTML = `<th></th><th class="text-end">Flock</th><th class="text-end">Stud</th><th class="text-end">Ram</th><th class="text-end">Other</th><th class="text-end">TOTAL</th>`;
		thead.appendChild(headerRow);
		table.appendChild(thead);

		// Table body
		const tbody = document.createElement('tbody');

		let roleTotal = 0;
		Object.values(summaryData).forEach(staff => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);
			const total = staff.runTotal || 0;
			roleTotal += total;

			row.innerHTML = `<td>${formattedName}</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">${total}</td>`;
			tbody.appendChild(row);
		});

		// Add total row
		if (Object.keys(summaryData).length > 0) {
			const totalRow = document.createElement('tr');
			totalRow.className = 'table-active fw-bold';
			totalRow.innerHTML = `<td>Total</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">0</td><td class="text-end">${roleTotal}</td>`;
			tbody.appendChild(totalRow);
		}

		table.appendChild(tbody);
		tableResponsive.appendChild(table);
		tableContainer.appendChild(tableResponsive);

		return tableContainer;
	}

	/**
	 * Render Presser summary table
	 */
	renderPresserSummaryTable() {
		// Get summary data for presser role
		const presserSummaryData = this.calculatePresserSummaryData();

		// Populate Presser tally table
		this.populatePresserSummaryTable('presserTally', presserSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Calculate summary data for Presser role
	 */
	calculatePresserSummaryData() {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'presser');
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						totals: {
							runs: 0,
							bales: 0,
							kilo: 0
						}
					};
				}

				// Sum up presser-specific values
				summaryData[id].totals.runs += parseFloat(staff.total_runs) || 0;
				summaryData[id].totals.bales += parseFloat(staff.total_bales) || 0;
				summaryData[id].totals.kilo += parseFloat(staff.total_kilo) || 0;
			});
		});

		return summaryData;
	}

	/**
	 * Populate Presser summary table
	 */
	populatePresserSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Calculate totals for each category
		const totals = {
			runs: 0,
			bales: 0,
			kilo: 0,
			total: 0
		};

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Calculate totals for this staff member
			const staffTotals = {
				runs: staff.totals.runs || 0,
				bales: staff.totals.bales || 0,
				kilo: staff.totals.kilo || 0
			};

			const staffTotal = staffTotals.runs + staffTotals.bales + staffTotals.kilo;

			// Add to grand totals
			totals.runs += staffTotals.runs;
			totals.bales += staffTotals.bales;
			totals.kilo += staffTotals.kilo;
			totals.total += staffTotal;

			// Calculate mess and evo counts for this staff member
			const mesEvoData = this.calculatePresserMessEvoCounts(staffId);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			row.innerHTML = `
				<td>
					<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
						<i class="far fa-angle-right"></i>
					</span>
					<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
				</td>
				<td class="text-end">${staffTotals.runs}</td>
				<td class="text-end">${staffTotals.bales}</td>
				<td class="text-end">${staffTotals.kilo}</td>
				<td class="text-end">${staffTotal}</td>
			`;
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			collapseRow.innerHTML = `
				<td colspan="5" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${mesEvoData.mess} | Travel: ${mesEvoData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (cells.length >= 5) {
					cells[1].textContent = totals.runs;
					cells[2].textContent = totals.bales;
					cells[3].textContent = totals.kilo;
					cells[4].textContent = totals.total;
				}
			}
		}
	}

	/**
	 * Calculate mess and evo counts for a Presser staff member
	 */
	calculatePresserMessEvoCounts(staffId) {
		let messCount = 0;
		let evoCount = 0;
		let travelCount = 0;

		// Use filtered data for mess/evo/travel calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'presser');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);

			if (staff) {
				messCount += parseFloat(staff.mess) || 0;
				evoCount += parseFloat(staff.evos) || 0;
				travelCount += parseFloat(staff.travel) || 0;
			}
		});

		return {mess: messCount, evo: evoCount, travel: travelCount};
	}

	/**
	 * Render Woolhandler summary table
	 */
	renderWoolhandlerSummaryTable() {
		// Get summary data for woolhandler role
		const woolhandlerSummaryData = this.calculateWoolhandlerSummaryData();

		// Populate Woolhandler tally table
		this.populateWoolhandlerSummaryTable('woolhandlerTally', woolhandlerSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Calculate summary data for Woolhandler role
	 */
	calculateWoolhandlerSummaryData() {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'woolhandler');
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						totalRuns: 0
					};
				}

				// Sum up woolhandler-specific values (just total_runs for Total column)
				summaryData[id].totalRuns += parseFloat(staff.total_runs) || 0;
			});
		});

		return summaryData;
	}

	/**
	 * Populate Woolhandler summary table
	 */
	populateWoolhandlerSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Calculate totals
		let grandTotal = 0;

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Calculate total for this staff member
			const staffTotal = staff.totalRuns || 0;
			grandTotal += staffTotal;

			// Calculate mess and evo counts for this staff member
			const mesEvoData = this.calculateWoolhandlerMessEvoCounts(staffId);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			row.innerHTML = `
				<td>
					<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
						<i class="far fa-angle-right"></i>
					</span>
					<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
				</td>
				<td class="text-end">${staffTotal}</td>
			`;
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			collapseRow.innerHTML = `
				<td colspan="3" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${mesEvoData.mess} | Travel: ${mesEvoData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (cells.length >= 2) {
					cells[1].textContent = grandTotal;
				}
			}
		}
	}

	/**
	 * Calculate mess and evo counts for a Woolhandler staff member
	 */
	calculateWoolhandlerMessEvoCounts(staffId) {
		let messCount = 0;
		let evoCount = 0;
		let travelCount = 0;

		// Use filtered data for mess/evo/travel calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'woolhandler');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);

			if (staff) {
				messCount += parseFloat(staff.mess) || 0;
				evoCount += parseFloat(staff.evos) || 0;
				travelCount += parseFloat(staff.travel) || 0;
			}
		});

		return {mess: messCount, evo: evoCount, travel: travelCount};
	}

	/**
	 * Render Classer summary table
	 */
	renderClasserSummaryTable() {
		// Get summary data for classer role
		const classerSummaryData = this.calculateClasserSummaryData();

		// Populate Classer tally table
		this.populateClasserSummaryTable('classerTally', classerSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Calculate summary data for Classer role
	 */
	calculateClasserSummaryData() {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'classer');
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						totals: {
							runs: 0,
							fleeces: 0
						}
					};
				}

				// Sum up classer-specific values
				summaryData[id].totals.runs += parseFloat(staff.total_runs) || 0;
				summaryData[id].totals.fleeces += parseFloat(staff.total_fleeces) || 0;
			});
		});

		return summaryData;
	}

	/**
	 * Populate Classer summary table
	 */
	populateClasserSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Calculate totals for each category
		const totals = {
			runs: 0,
			fleeces: 0,
			total: 0
		};

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Calculate totals for this staff member
			const staffTotals = {
				runs: staff.totals.runs || 0,
				fleeces: staff.totals.fleeces || 0
			};

			const staffTotal = staffTotals.runs + staffTotals.fleeces;

			// Add to grand totals
			totals.runs += staffTotals.runs;
			totals.fleeces += staffTotals.fleeces;
			totals.total += staffTotal;

			// Calculate mess and evo counts for this staff member
			const mesEvoData = this.calculateClasserMessEvoCounts(staffId);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			row.innerHTML = `
				<td>
					<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
						<i class="far fa-angle-right"></i>
					</span>
					<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
				</td>
				<td class="text-end">${staffTotals.runs}</td>
				<td class="text-end">${staffTotals.fleeces}</td>
				<td class="text-end">${staffTotal}</td>
			`;
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			collapseRow.innerHTML = `
				<td colspan="4" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${mesEvoData.mess} | Travel: ${mesEvoData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (cells.length >= 4) {
					cells[1].textContent = totals.runs;
					cells[2].textContent = totals.fleeces;
					cells[3].textContent = totals.total;
				}
			}
		}
	}

	/**
	 * Calculate mess and evo counts for a Classer staff member
	 */
	calculateClasserMessEvoCounts(staffId) {
		let messCount = 0;
		let evoCount = 0;
		let travelCount = 0;

		// Use filtered data for mess/evo/travel calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'classer');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);

			if (staff) {
				messCount += parseFloat(staff.mess) || 0;
				evoCount += parseFloat(staff.evos) || 0;
				travelCount += parseFloat(staff.travel) || 0;
			}
		});

		return {mess: messCount, evo: evoCount, travel: travelCount};
	}

	/**
	 * Render Sheepo summary table
	 */
	renderSheepoSummaryTable() {
		// Get summary data for sheepo role
		const sheepoSummaryData = this.calculateSheepoSummaryData();

		// Populate Sheepo tally table
		this.populateSheepoSummaryTable('sheepoTally', sheepoSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Calculate summary data for Sheepo role
	 */
	calculateSheepoSummaryData() {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'sheepo');
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						totalRuns: 0
					};
				}

				// Sum up sheepo-specific values (just total_runs for Total column)
				summaryData[id].totalRuns += parseFloat(staff.total_runs) || 0;
			});
		});

		return summaryData;
	}

	/**
	 * Populate Sheepo summary table
	 */
	populateSheepoSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Calculate totals
		let grandTotal = 0;

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Calculate total for this staff member
			const staffTotal = staff.totalRuns || 0;
			grandTotal += staffTotal;

			// Calculate mess and evo counts for this staff member
			const mesEvoData = this.calculateSheepoMessEvoCounts(staffId);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			row.innerHTML = `
				<td>
					<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
						<i class="far fa-angle-right"></i>
					</span>
					<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
				</td>
				<td class="text-end">${staffTotal}</td>
			`;
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			collapseRow.innerHTML = `
				<td colspan="2" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${mesEvoData.mess} | Travel: ${mesEvoData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (cells.length >= 2) {
					cells[1].textContent = grandTotal;
				}
			}
		}
	}

	/**
	 * Calculate mess and evo counts for a Sheepo staff member
	 */
	calculateSheepoMessEvoCounts(staffId) {
		let messCount = 0;
		let evoCount = 0;
		let travelCount = 0;

		// Use filtered data for mess/evo/travel calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'sheepo');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);

			if (staff) {
				messCount += parseFloat(staff.mess) || 0;
				evoCount += parseFloat(staff.evos) || 0;
				travelCount += parseFloat(staff.travel) || 0;
			}
		});

		return {mess: messCount, evo: evoCount, travel: travelCount};
	}

	/**
	 * Render Cook summary table
	 */
	renderCookSummaryTable() {
		// Get summary data for cook role
		const cookSummaryData = this.calculateCookSummaryData();

		// Populate Cook tally table
		this.populateCookSummaryTable('cookTally', cookSummaryData);

		// Bind collapse events for icon rotation
		this.bindCollapseEvents();
	}

	/**
	 * Calculate summary data for Cook role
	 */
	calculateCookSummaryData() {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'cook');
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						totalPerDay: 0,
						totalPerHead: 0
					};
				}

				// Sum up cook-specific values based on payment type
				const count = parseFloat(staff.count) || 0;
				if (staff.payment_type === 'per_head') {
					summaryData[id].totalPerHead += count;
				} else {
					summaryData[id].totalPerDay += count;
				}
			});
		});

		return summaryData;
	}

	/**
	 * Populate Cook summary table
	 */
	populateCookSummaryTable(tableId, summaryData) {
		const table = document.querySelector(`#${tableId} tbody`);
		const tfoot = document.querySelector(`#${tableId} tfoot`);
		if (!table) return;

		// Clear existing content
		table.innerHTML = '';

		// Calculate totals
		let totals = {
			perDay: 0,
			perHead: 0
		};

		// Create rows for each staff member
		Object.entries(summaryData).forEach(([staffId, staff]) => {
			const row = document.createElement('tr');

			// Format name as first initial + last name
			const formattedName = this.formatStaffName(staff.name);

			// Sum up totals
			totals.perDay += staff.totalPerDay || 0;
			totals.perHead += staff.totalPerHead || 0;

			// Calculate mess and travel count for this staff member
			const messData = this.calculateCookMessTravelCount(staffId);

			// Generate unique collapse ID
			const collapseId = `collapse-${tableId}-${staffId}`;

			row.innerHTML = `
				<td>
					<span class="collapse-icon me-1" style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">
						<i class="far fa-angle-right"></i>
					</span>
					<span style="cursor: pointer;" data-bs-toggle="collapse" data-bs-target="#${collapseId}" aria-expanded="false">${formattedName}</span>
				</td>
				<td class="text-end">${staff.totalPerDay || 0}</td>
				<td class="text-end">${staff.totalPerHead || 0}</td>
			`;
			table.appendChild(row);

			// Add collapse row
			const collapseRow = document.createElement('tr');
			collapseRow.innerHTML = `
				<td colspan="3" class="p-0">
					<div class="collapse" id="${collapseId}">
						<div class="p-2 bg-light border-top">
							<small>Mess: ${messData.mess} | Travel: ${messData.travel}</small>
						</div>
					</div>
				</td>
			`;
			table.appendChild(collapseRow);
		});

		// Update footer totals
		if (tfoot) {
			const footerRow = tfoot.querySelector('tr');
			if (footerRow) {
				const cells = footerRow.querySelectorAll('td');
				if (cells.length >= 3) {
					cells[1].textContent = totals.perDay;
					cells[2].textContent = totals.perHead;
				}
			}
		}
	}

	/**
	 * Calculate mess and travel count for a Cook staff member
	 */
	calculateCookMessTravelCount(staffId) {
		let messCount = 0;
		let travelCount = 0;

		// Use filtered data for calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'cook');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);

			if (staff) {
				messCount += parseFloat(staff.mess) || 0;
				travelCount += parseFloat(staff.travel) || 0;
			}
		});

		return {mess: messCount, travel: travelCount};
	}

	/**
	 * Bind collapse events for icon rotation
	 */
	bindCollapseEvents() {
		// No complex management needed - CSS handles Unicode triangle rotation automatically
	}

	/**
	 * Calculate mess and evo counts for a staff member in a specific section
	 */
	calculateMessEvoCounts(staffId, section) {
		let messCount = 0;
		let evoCount = 0;
		let travelCount = 0;

		// Use filtered data for mess/evo/travel calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === 'shearing_crutching');
			if (!roleData) return;

			// Find staff member in this day
			const staff = roleData.staff_counts.find(s =>
				s.staff_id === staffId &&
				(section === 'SHEARING' ?
					(s.originalRole === 'Shearing' || s.originalRole === 'Shearers') :
					(s.originalRole === 'Crutcher' || s.originalRole === 'Crutchers' || s.originalRole === 'Shearing' || s.originalRole === 'Shearers'))
			);

			if (staff) {
				// Use section-specific values for shearing/crutching
				if (section === 'SHEARING') {
					messCount += parseFloat(staff.shearing_mess) || 0;
					evoCount += parseFloat(staff.shearing_evos) || 0;
					travelCount += parseFloat(staff.shearing_travel) || 0;
				} else if (section === 'CRUTCHING') {
					messCount += parseFloat(staff.crutching_mess) || 0;
					evoCount += parseFloat(staff.crutching_evos) || 0;
					travelCount += parseFloat(staff.crutching_travel) || 0;
				}
			}
		});

		return {mess: messCount, evo: evoCount, travel: travelCount};
	}

	/**
	 * Format staff name as first initial + last name
	 */
	formatStaffName(fullName) {
		const nameParts = fullName.trim().split(' ');
		if (nameParts.length < 2) return fullName;

		const firstInitial = nameParts[0].charAt(0).toUpperCase();
		const lastName = nameParts[nameParts.length - 1];

		return `${firstInitial} ${lastName}`;
	}

	/**
	 * Calculate summary data for current role
	 */
	calculateSummaryData(roleId) {
		const summaryData = {};

		// Use filtered data for summary calculations
		const filteredData = this.getFilteredTallyData();

		filteredData.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData) return;

			roleData.staff_counts.forEach(staff => {
				const id = staff.staff_id;
				if (!summaryData[id]) {
					summaryData[id] = {
						name: staff.staff_name,
						travelCount: 0,
						runTotal: 0,
						hoursTotal: 0,
						amountTotal: 0
					};
				}

				// Sum up values based on role type
				if (staff.runs && Array.isArray(staff.runs)) {
					staff.runs.forEach(run => {
						const count = parseFloat(run.count) || 0;
						summaryData[id].runTotal += count;
					});
				}

				if (staff.hours) {
					summaryData[id].hoursTotal += parseFloat(staff.hours) || 0;
				}

				if (staff.amount) {
					summaryData[id].amountTotal += parseFloat(staff.amount) || 0;
				}

				if (staff.travel) {
					summaryData[id].travelCount += parseFloat(staff.travel) || 0;
				}
			});
		});

		return summaryData;
	}

	/**
	 * Check if a role has any staff assigned OR exists in tally data
	 */
	roleHasStaff(roleId) {
		if (!this.talliesData || !this.talliesData.tally_data) return false;

		// Both tally-only and authenticated access check for staff
		return this.talliesData.tally_data.some(day => {
			const roleData = day.roles.find(r => r.role_id === roleId);
			return roleData && roleData.staff_counts && roleData.staff_counts.length > 0;
		});
	}

	/**
	 * Add staff to a role
	 */
	addStaffToRole(staffId, staffName, roleId, originalRole = null) {
		const defaultStaff = getDefaultStaffEntry(roleId, staffId, staffName);
		if (!defaultStaff) return;

		// Store original role for section mapping
		if (originalRole) {
			defaultStaff.originalRole = originalRole;
		}

		this.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData) return;

			// Check if staff already exists with the same original role
			const existingStaff = roleData.staff_counts.find(s => s.staff_id === staffId && s.originalRole === originalRole);

			if (existingStaff) {
				// Staff exists - if they were removed, reactivate them
				if (existingStaff.removed) {
					delete existingStaff.removed;
				}
			} else {
				// Staff doesn't exist - add them
				const staffEntry = {...defaultStaff};

				// Initialize section-specific runs for shearing_crutching role
				if (roleId === 'shearing_crutching' && originalRole && roleData.runs) {
					// Initialize shearing_runs and crutching_runs based on original role
					if (originalRole === 'Shearing' || originalRole === 'Shearers') {
						staffEntry.shearing_runs = roleData.runs.map(run => ({
							run: run.id,
							count: '0'
						}));
					}

					if (originalRole === 'Crutcher' || originalRole === 'Crutchers' || originalRole === 'Shearing' || originalRole === 'Shearers') {
						staffEntry.crutching_runs = roleData.runs.map(run => ({
							run: run.id,
							count: '0'
						}));
					}
				}

				roleData.staff_counts.push(staffEntry);
			}
		});

		// Re-render navigation to show/hide tabs based on staff allocation
		this.renderRoleNavigation();

		// Re-render if this is the current role
		if (this.currentRoleId === roleId) {
			this.renderTablesForRole(roleId);
		}

		// Always re-render all summary tables after adding staff
		this.renderAllSummaryTables();
		this.syncToHiddenInput();
	}

	/**
	 * Hide an element by selector
	 */
	hideElement(selector) {
		const element = document.querySelector(selector);
		if (element) {
			element.style.display = 'none';
		}
	}

	/**
	 * Show an element by selector
	 */
	showElement(selector) {
		const element = document.querySelector(selector);
		if (element) {
			element.style.display = '';
		}
	}

	/**
	 * Sync data to hidden input for form submission
	 */
	syncToHiddenInput() {
		// Try both IDs for compatibility
		let hiddenInput = document.getElementById('id_job-tallies_json') || document.getElementById('talliesData');
		if (hiddenInput && this.talliesData) {
			hiddenInput.value = JSON.stringify(this.talliesData);
		}

		// Update global reference for backwards compatibility
		window.talliesData = this.talliesData;
	}

	/**
	 * Check if a single staff entry has zero tally data
	 */
	staffEntryHasZeroData(staff, roleId) {
		if (!staff) return true;

		switch (roleId) {
			case 'shearing_crutching': {
				// Check shearing runs
				const shearingTotal = (staff.shearing_runs || []).reduce((sum, run) => sum + (parseInt(run.count) || 0), 0);
				// Check crutching runs
				const crutchingTotal = (staff.crutching_runs || []).reduce((sum, run) => sum + (parseInt(run.count) || 0), 0);
				const shearingMess = parseInt(staff.shearing_mess) || 0;
				const shearingEvos = parseInt(staff.shearing_evos) || 0;
				const shearingTravel = parseInt(staff.shearing_travel) || 0;
				const crutchingMess = parseInt(staff.crutching_mess) || 0;
				const crutchingEvos = parseInt(staff.crutching_evos) || 0;
				const crutchingTravel = parseInt(staff.crutching_travel) || 0;
				return shearingTotal === 0 && crutchingTotal === 0 &&
					shearingMess === 0 && shearingEvos === 0 && shearingTravel === 0 &&
					crutchingMess === 0 && crutchingEvos === 0 && crutchingTravel === 0;
			}
			case 'presser': {
				const runs = parseInt(staff.total_runs) || 0;
				const bales = parseInt(staff.total_bales) || 0;
				const kilo = parseInt(staff.total_kilo) || 0;
				const mess = parseInt(staff.mess) || 0;
				const travel = parseInt(staff.travel) || 0;
				return runs === 0 && bales === 0 && kilo === 0 && mess === 0 && travel === 0;
			}
			case 'classer': {
				const runs = parseInt(staff.total_runs) || 0;
				const fleeces = parseInt(staff.total_fleeces) || 0;
				const mess = parseInt(staff.mess) || 0;
				const travel = parseInt(staff.travel) || 0;
				return runs === 0 && fleeces === 0 && mess === 0 && travel === 0;
			}
			case 'woolhandler':
			case 'sheepo': {
				const runs = parseInt(staff.total_runs) || 0;
				const mess = parseInt(staff.mess) || 0;
				const travel = parseInt(staff.travel) || 0;
				return runs === 0 && mess === 0 && travel === 0;
			}
			case 'cook': {
				const count = parseInt(staff.count) || 0;
				const mess = parseInt(staff.mess) || 0;
				const travel = parseInt(staff.travel) || 0;
				return count === 0 && mess === 0 && travel === 0;
			}
			default:
				return true;
		}
	}

	/**
	 * Check if a staff member has zero tally data across ALL days of the job
	 */
	staffHasZeroDataAllDays(staffId, roleId) {
		if (!this.talliesData || !this.talliesData.tally_data) return true;

		// Check every day
		for (const day of this.talliesData.tally_data) {
			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData || !roleData.staff_counts) continue;

			const staff = roleData.staff_counts.find(s => s.staff_id === staffId);
			if (staff && !this.staffEntryHasZeroData(staff, roleId)) {
				// Found non-zero data on at least one day
				return false;
			}
		}

		return true;
	}

	/**
	 * Remove a staff member from ALL days for a role
	 */
	removeStaffFromRole(staffId, roleId) {
		if (!this.talliesData || !this.talliesData.tally_data) return;

		// Convert staffId to string for comparison (tallies store IDs as strings)
		const staffIdStr = String(staffId);

		// Remove from all days
		this.talliesData.tally_data.forEach(day => {
			const roleData = day.roles.find(r => r.role_id === roleId);
			if (!roleData || !roleData.staff_counts) return;

			const staffIndex = roleData.staff_counts.findIndex(s => String(s.staff_id) === staffIdStr);
			if (staffIndex !== -1) {
				roleData.staff_counts.splice(staffIndex, 1);
			}
		});

		// Re-render the tables for this role
		this.renderTablesForRole(roleId);

		// Update summary tables
		this.renderAllSummaryTables();

		// Sync to hidden input
		this.syncToHiddenInput();
	}
}

// Initialize the system
const jobTalliesManager = new JobTalliesManager();

// Make jobTalliesManager globally accessible for template scripts
window.jobTalliesManager = jobTalliesManager;

// Legacy function exports for backwards compatibility
const logTallies = () => {
	jobTalliesManager.init();
};

// Listen for staff added events
Ajax.on('staffAdded', ({staffId, staffName, roleId, roleName}) => {
	// Skip if staff allocation watcher is active - it will handle the addition with proper originalRole
	if (window.staffAllocationWatcher) {
		return;
	}

	// Map legacy role names to new role IDs if needed
	const mappedRoleId = mapLegacyRoleId(roleId, roleName);
	jobTalliesManager.addStaffToRole(staffId, staffName, mappedRoleId);
});

/**
 * Map legacy role IDs to new hardcoded role IDs
 */
function mapLegacyRoleId(oldRoleId, roleName) {
	// This function maps old dynamic role IDs to new hardcoded ones
	// You can customize this based on your role naming conventions

	const lowerRoleName = roleName.toLowerCase();

	if (lowerRoleName.includes('shear') || lowerRoleName.includes('crutch') || lowerRoleName === 'shearers') {
		return 'shearing_crutching';
	} else if (lowerRoleName.includes('press')) {
		return 'presser';
	} else if (lowerRoleName.includes('wool') || lowerRoleName.includes('handl')) {
		return 'woolhandler';
	} else if (lowerRoleName.includes('class')) {
		return 'classer';
	} else if (lowerRoleName.includes('sheepo')) {
		return 'sheepo';
	}

	// Default to shearing & crutching if no match
	return 'shearing_crutching';
}

export default logTallies;
export {jobTalliesManager};